/**
 * useSubscription hook
 *
 * Provides subscription status and upgrade functions to components.
 *
 * A failed status check never pitches a trial (review of #3988/#4031/#4062): a status
 * already shown for this account stays; a first load restores this account's last
 * saved status (utils/subscriptionSnapshot.ts, marked `statusStale`); with none the
 * status is UNAVAILABLE (`isLoading` stays true, `statusUnavailable` true — surfaces
 * stay neutral and offer "Try again"). While stale or unavailable the hook keeps
 * retrying, backing off with jitter, and again when the app comes to the foreground.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AppState, Platform } from 'react-native';
import { subscriptionService, SubscriptionTier, SubscriptionPeriod } from '@/services/subscriptionService';
import { useAuth } from '@/contexts/AuthContext';
import { secureTokenStorage } from '@/services/secureTokenStorage';
import { answerFromSubscriptionStatus, forgetOnboardingAnswer, recordOnboardingAnswer } from '../services/onboardingAnswer';
import { getApiBaseUrl } from '@/utils/apiConfig';
import { mirrorCedarCohort, type CedarCohort, type PaywallProvider, type PaywallVariables } from '@/services/revenueCatPaywall';
import { subscribeSubscriptionStatusChanged } from '@/services/subscriptionStatusBus';
import type { PartnerOfferSummary } from '@/utils/partnerOfferSheet';
import {
  mergeQueuedRefresh,
  refreshKeepsStatusOnError,
  refreshShowsLoading,
  type SubscriptionRefreshOptions,
} from '@/utils/subscriptionRefresh';
import { storage } from '@/services/storage';
import { decodeJwtPayload } from '@/utils/jwtExpiry';
import {
  decodeSnapshot,
  encodeSnapshot,
  failedCheckOutcome,
  retryDelayMs,
  snapshotKey,
} from '@/utils/subscriptionSnapshot';

/**
 * Subscription cohort — the single source of truth for every subscription UI
 * surface (Account CTA, chat dead-chat strip, welcome pill). Mirrors the enum
 * defined in services/subscription_cohort.py and computed on the backend.
 */
export type SubscriptionCohort =
  | 'pre_trial'
  | 'trial_active'
  | 'subscription_expired'
  | 'paid_active'
  | 'paid_cancelled_grace'
  | 'billing_issue'
  | 'unknown';

/** One parsed subscription-status response (the shared single flight below). */
interface StatusFetchResult {
  ok: boolean;
  httpStatus: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  text: string;
}

/**
 * The status fetch in flight, per account (review of #4062): every screen holds its
 * own copy of this hook — a cold start mounts two chat screens beside Account, the
 * Subscription drawer and the paywall — and each checks, and retries, on its own.
 * Instances asking for the same account while a fetch is in flight share it: one
 * request, not one per screen. The entry is removed the moment the fetch settles, so
 * the next check always asks the server again.
 */
const statusFetchesInFlight = new Map<string, Promise<StatusFetchResult>>();

function fetchStatusShared(key: string, url: string, headers: HeadersInit): Promise<StatusFetchResult> {
  const existing = statusFetchesInFlight.get(key);
  if (existing) return existing;
  const shared: Promise<StatusFetchResult> = (async () => {
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      return { ok: false, httpStatus: response.status, data: null, text: await response.text() };
    }
    return { ok: true, httpStatus: response.status, data: await response.json(), text: '' };
  })().finally(() => {
    if (statusFetchesInFlight.get(key) === shared) statusFetchesInFlight.delete(key);
  });
  statusFetchesInFlight.set(key, shared);
  return shared;
}

/**
 * A refused status request (a 4xx other than 408 timeout / 429 rate limit): retrying on
 * a timer gets the same answer. Exported for tests.
 */
export function failedHttpStatusIsPermanent(status: number | null): boolean {
  return status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/** The loaded-status key for a request that carries no account id. */
const NO_ACCOUNT_ID = '__no_account_id__';

// Behaviour pinned by tests (web/src/hooks/__tests__/useSubscription.firstLoad.test.tsx):
//   - a failed first load restores this account's saved status, else goes UNAVAILABLE
//     (never a trial pitch), and keeps retrying while stale or unavailable;
//   - a status is "already loaded" per account: another account's is never shown;
//   - retries stop at unmount; instances share one in-flight fetch per account.
export function useSubscription() {
  const { user: currentUser } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('trial');
  const [status, setStatus] = useState<'active' | 'trial' | 'cancelled' | 'expired' | 'free'>('trial');
  const [cohort, setCohort] = useState<SubscriptionCohort>('pre_trial');
  const [isLoading, setIsLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState<Date | null>(null);
  const [trialStartsAt, setTrialStartsAt] = useState<Date | null>(null);
  const [isBetaTester, setIsBetaTester] = useState(false);
  // Trial boost: Essentials/deferred-trial users get Chat+ feature parity
  // (uploads, watch push, Chat+ guidance) for the 7-day trial window.
  // Backend-authoritative — flips False the moment trial_expires_at passes.
  const [trialBoostActive, setTrialBoostActive] = useState(false);
  const [periodType, setPeriodType] = useState<SubscriptionPeriod>('monthly');
  // Backend-authoritative onboarding state — used by trial gate to verify
  // step against live data instead of stale AsyncStorage backendState.
  const [onboardingStep, setOnboardingStep] = useState<number>(0);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean>(false);
  // 🆕 Trial-gate-at-goal-confirm (sprint 2026-05-22). Backend-authoritative.
  // trialGateAtGoalConfirm: the new flow is active for this user → the chat
  //   screen suppresses the legacy step-7 paywall trigger.
  // trialGateRequired: a goal is parked at 'awaiting_trial' → a returning unpaid
  //   user must re-see the trial gate.
  const [trialGateAtGoalConfirm, setTrialGateAtGoalConfirm] = useState<boolean>(false);
  // 🕖 Seven Free Days (Wave 3, OTA-A). Backend-authoritative, per-user aware.
  // timeBoundTrial: the 7-day clock model is active for this user → every trial
  //   surface renders the "Your 7 days with Cedar are done. Continue with Cedar."
  //   family (web/src/utils/sevenFreeDaysCopy.ts) and the goal-confirm paywall
  //   trigger (ChatScreen Effect 3) stands down — the backend starts the clock
  //   instead of parking. Absent/false → legacy copy byte-for-byte.
  const [timeBoundTrial, setTimeBoundTrial] = useState<boolean>(false);
  // lapsedPaid (store-day prep, 2026-09-26): an expired user who once PAID. The
  //   expired surfaces then read "Re-subscribe to continue with your coach"
  //   instead of the finished-free-week copy. Absent/false → unchanged.
  const [lapsedPaid, setLapsedPaid] = useState<boolean>(false);
  // freeTrialUsed (journey ruling J20, 2026-09-27): a returning identity — its free period
  //   was on an earlier account — can never start the clock. Its pre-trial surfaces say
  //   "You've already had your free trial with Coach Cedar." Absent/false → unchanged.
  const [freeTrialUsed, setFreeTrialUsed] = useState<boolean>(false);
  // 🧭 Seven Free Days Wave 6 (Build B): which paywall this build shows is the
  // SERVER's call (`paywall_provider`: legacy | revenuecat — the build-number
  // gate lives there); `cedar_cohort` is the RevenueCat targeting vocabulary the
  // backend stamps and the app mirrors; `paywall_variables` is spec §3.
  const [paywallProvider, setPaywallProvider] = useState<PaywallProvider>('legacy');
  const [cedarCohort, setCedarCohort] = useState<CedarCohort>('unknown');
  const [paywallVariables, setPaywallVariables] = useState<PaywallVariables | null>(null);
  const [trialGateRequired, setTrialGateRequired] = useState<boolean>(false);
  // 🌞 Summer50 — flag exposure (client can't read ECS env) + free-month state + reward count.
  const [summer50, setSummer50] = useState<{ codes_enabled: boolean; referral_enabled: boolean }>({ codes_enabled: false, referral_enabled: false });
  // `family` + `duration_label` (added with the strategic code family) let surfaces pick
  // campaign vs neutral copy and state the real grant length. Optional so an older
  // backend response — or the error fallback — simply reads as the campaign default.
  const [freeMonth, setFreeMonth] = useState<{
    active: boolean;
    starts_at: string | null;
    expires_at: string | null;
    closed_reason: string | null;
    family?: 'summer50' | 'strategic' | null;
    duration_label?: string | null;
  } | null>(null);
  // storeFreeMonth (free month PR-5, contract assumed): a STORE PAYER's free month moved their
  //   next charge back — `store_free_month: {store, next_charge_at}` from the status answer.
  //   Account shows "Free month from Coach Cedar · next charge {date}". Null when absent.
  const [storeFreeMonth, setStoreFreeMonth] = useState<{
    store: 'app_store' | 'play_store' | null;
    next_charge_at: string | null;
  } | null>(null);
  const [rewardsReady, setRewardsReady] = useState<number>(0);
  // trialDaysLeft (review of #3988, 2026-09-26): the server's calendar count of an
  //   active trial's days left, in the user's own zone (users.timezone — where the
  //   clock was cut). Account prefers it to the phone's own count; null when absent.
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  // trialLengthDays (partner codes build item 2, 2026-09-27): the free week's length — 7,
  //   or 14 / 30 when a partner code lengthened it. Caps the days-left count.
  const [trialLengthDays, setTrialLengthDays] = useState<number>(7);
  // partnerOffer (partner codes build item 5, 2026-09-27): the partner offer this person
  //   holds (`partner_offer` — its two discount levels), or null. The paywall asks the server
  //   for a discount to buy with only when a level is set (hooks/usePartnerOfferGate.ts); null
  //   for everyone while the partner program is dark.
  const [partnerOffer, setPartnerOffer] = useState<PartnerOfferSummary | null>(null);
  // Guard against concurrent in-flight subscription checks
  const checkInFlightRef = useRef(false);
  // When a check is dropped because one is already in flight, queue a re-check
  // so the latest user state is always fetched (e.g. rapid logout/login).
  const recheckNeededRef = useRef(false);
  // The options the queued re-check runs with (background only if every request
  // that queued it asked for background — utils/subscriptionRefresh.ts).
  const recheckOptionsRef = useRef<SubscriptionRefreshOptions | null>(null);
  // Depth counter to prevent unbounded recursion of re-checks
  const recheckCountRef = useRef(0);
  // The account whose status is on screen (review of #3988, keyed by account in the
  // review of #4062): for THAT account a BACKGROUND re-check keeps showing it instead
  // of the loading state, and a failed check keeps it. Any other account's check is a
  // first load — on a shared phone, A's status is never shown to B. Reset on sign-out.
  const loadedAccountRef = useRef<string | null>(null);
  // The signal of the check that queued the re-check: a queued re-check honours the
  // cancellation of the screen that asked for it (unmount / signed-in user changed).
  const recheckSignalRef = useRef<{ cancelled: boolean } | null>(null);
  // A first load that failed with nothing to show (utils/subscriptionSnapshot.ts):
  // the status is UNAVAILABLE — surfaces stay neutral, Account offers a retry, and the
  // hook retries on its own. `statusStale`: what is shown is this account's last saved
  // status, not a fresh answer.
  const [statusUnavailable, setStatusUnavailable] = useState(false);
  const [statusStale, setStatusStale] = useState(false);
  // Mirrors of the two states for timers and the AppState listener (closures see refs).
  const staleRef = useRef(false);
  const unavailableRef = useRef(false);
  // "Try again" is in progress — the button shows a busy state.
  const [retrying, setRetrying] = useState(false);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // This instance has unmounted: its retry timers and listeners stop. A check started
  // without a signal (the bus, "Try again", the foreground refresh) can otherwise
  // outlive the screen that asked for it.
  const unmountedRef = useRef(false);
  // The latest checkSubscription, for timers and listeners set up by an earlier render.
  const checkRef = useRef<(signal?: { cancelled: boolean }, options?: SubscriptionRefreshOptions) => Promise<void>>(
    async () => undefined,
  );

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  /**
   * A fresh answer is still owed (nothing shown, or a saved status shown): check again
   * after the next back-off step (utils/subscriptionSnapshot.retryDelayMs, jittered).
   * A stale status is re-checked in the background so it stays on screen meanwhile.
   */
  const scheduleRetry = useCallback((signal?: { cancelled: boolean }) => {
    if (unmountedRef.current || signal?.cancelled) return;
    const delay = retryDelayMs(retryAttemptRef.current);
    retryAttemptRef.current += 1;
    clearRetryTimer();
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      if (unmountedRef.current || signal?.cancelled) return;
      void checkRef.current(signal, staleRef.current ? { background: true } : undefined);
    }, delay);
  }, [clearRetryTimer]);

  /** No status for the account being checked: nothing another account's may linger. */
  const resetStatusToDefaults = useCallback(() => {
    setTier('trial');
    setStatus('trial');
    setCohort('pre_trial');
    setExpiresAt(null);
    setTrialExpiresAt(null);
    setTrialStartsAt(null);
    setIsBetaTester(false);
    setTrialBoostActive(false);
    setPeriodType('monthly');
    setOnboardingStep(0);
    setOnboardingCompleted(false);
    setTrialGateAtGoalConfirm(false);
    setTrialGateRequired(false);
    setTimeBoundTrial(false);
    setLapsedPaid(false);
    setFreeTrialUsed(false);
    setPaywallProvider('legacy');
    setCedarCohort('unknown');
    setPaywallVariables(null);
    setSummer50({ codes_enabled: false, referral_enabled: false });
    setFreeMonth(null);
    setStoreFreeMonth(null);
    setRewardsReady(0);
    setTrialDaysLeft(null);
    setTrialLengthDays(7);
    setPartnerOffer(null);
  }, []);

  /** Every field a subscription-status answer sets — from the server, or restored
   *  from this account's saved status (utils/subscriptionSnapshot.ts). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const applyStatus = useCallback((data: any) => {
    setTier(data.tier);
    setStatus(data.status);
    setCohort((data.cohort as SubscriptionCohort) ?? 'unknown');
    setExpiresAt(data.subscription_ends_at ? new Date(data.subscription_ends_at) : null);
    setTrialExpiresAt(data.trial_expires_at ? new Date(data.trial_expires_at) : null);
    setTrialStartsAt(data.trial_starts_at ? new Date(data.trial_starts_at) : null);
    setIsBetaTester(data.is_beta_tester);
    setTrialBoostActive(data.trial_boost_active === true);
    setPeriodType(data.period_type === 'annual' ? 'annual' : 'monthly');
    setOnboardingStep(data.onboarding_step ?? 0);
    setOnboardingCompleted(data.onboarding_completed ?? false);
    setTrialGateAtGoalConfirm(data.trial_gate_at_goal_confirm === true);
    setTrialGateRequired(data.trial_gate_required === true);
    setTimeBoundTrial(data.time_bound_trial === true);
    setLapsedPaid(data.lapsed_paid === true);
    setFreeTrialUsed(data.free_trial_used === true);
    setPaywallProvider(data.paywall_provider === 'revenuecat' ? 'revenuecat' : 'legacy');
    setCedarCohort((data.cedar_cohort as CedarCohort) ?? 'unknown');
    setPaywallVariables(data.paywall_variables && typeof data.paywall_variables === 'object' ? data.paywall_variables : null);
    // 🌞 Summer50 fields (best-effort defaults if the backend fields are absent).
    setSummer50(data.summer50 ?? { codes_enabled: false, referral_enabled: false });
    setFreeMonth(data.free_month ?? null);
    setStoreFreeMonth(data.store_free_month && typeof data.store_free_month === 'object'
      && typeof data.store_free_month.next_charge_at === 'string' ? {
      store: data.store_free_month.store === 'app_store' || data.store_free_month.store === 'play_store'
        ? data.store_free_month.store : null,
      next_charge_at: data.store_free_month.next_charge_at,
    } : null);
    setRewardsReady(typeof data.rewards_ready === 'number' ? data.rewards_ready : 0);
    setTrialDaysLeft(typeof data.trial_days_left === 'number' ? data.trial_days_left : null);
    setTrialLengthDays(typeof data.trial_length_days === 'number' ? data.trial_length_days : 7);
    setPartnerOffer(data.partner_offer && typeof data.partner_offer === 'object' ? {
      discount_monthly_pct: typeof data.partner_offer.discount_monthly_pct === 'number' ? data.partner_offer.discount_monthly_pct : null,
      discount_annual_pct: typeof data.partner_offer.discount_annual_pct === 'number' ? data.partner_offer.discount_annual_pct : null,
      discount_purchase_live: data.partner_offer.discount_purchase_live === true,
      // Codes PR-2 (optional): the discounts' terms; absent from a server before it.
      discount_monthly_months: typeof data.partner_offer.discount_monthly_months === 'number' ? data.partner_offer.discount_monthly_months : null,
      discount_annual_months: typeof data.partner_offer.discount_annual_months === 'number' ? data.partner_offer.discount_annual_months : null,
      discount_monthly_withheld: data.partner_offer.discount_monthly_withheld === true,
    } : null);
  }, []);

  // Stable reference to checkSubscription with cancellation support
  const checkSubscription = useCallback(async (
    signal?: { cancelled: boolean },
    options?: SubscriptionRefreshOptions,
  ) => {
    // Prevent concurrent checks — queue a re-check instead of silently dropping
    if (checkInFlightRef.current) {
      recheckNeededRef.current = true;
      recheckOptionsRef.current = mergeQueuedRefresh(recheckOptionsRef.current, options);
      if (signal) recheckSignalRef.current = signal;
      return;
    }
    checkInFlightRef.current = true;
    let accountId: string | null = null;
    // The loaded-status key of the account this check is for, and whether its status
    // is the one on screen (known once the credential is read).
    let accountKey = NO_ACCOUNT_ID;
    let loadedForThisAccount = false;
    // The HTTP status of a failed answer (null: no answer — offline, timeout, parse).
    let failedHttpStatus: number | null = null;
    // A failed first load with nothing to show keeps the loading state (never a trial pitch).
    let stayLoading = false;

    if (__DEV__) console.log('📦 SUBSCRIPTION: checkSubscription() started');

    try {
      // For native, we need explicit auth headers and full URL
      let url = '/api/user/subscription-status';
      let headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      // The credential this answer is fetched with — its `sub` is the account
      // the onboarding answer belongs to (services/onboardingAnswer.ts).
      let cedarJwt: string | null = null;
      // The account a saved status belongs to (utils/subscriptionSnapshot.ts).
      accountId = Platform.OS === 'web' ? (currentUser?.id ?? null) : null;

      if (Platform.OS !== 'web') {
        const token = await secureTokenStorage.getCedarJwt();
        cedarJwt = token;
        const sub = token ? decodeJwtPayload(token)?.sub : null;
        accountId = typeof sub === 'string' && sub ? sub : null;

        if (!token) {
          // No account, no answer: the defaults reset below are NOT an
          // onboarding answer (step 0 / not completed would read as "new user").
          forgetOnboardingAnswer();
          // No JWT = logged out or pre-auth — silently reset to defaults
          // instead of throwing (which spams console errors on logout). Every
          // field, through the one reset (review of #4062): a leftover expiresAt
          // or isBetaTester would make hasActiveTrialOrSubscription true for the
          // next account. The trial-gate signals are cleared with the rest, so a
          // stale parked-goal flag can't fire the paywall on the logged-out /
          // pre-auth screen (trial+pre_trial would otherwise match the gate).
          resetStatusToDefaults();
          loadedAccountRef.current = null;
          staleRef.current = false;
          unavailableRef.current = false;
          setStatusUnavailable(false);
          setStatusStale(false);
          clearRetryTimer();
          setIsLoading(false);
          checkInFlightRef.current = false;
          return;
        }

        const baseUrl = getApiBaseUrl();
        url = `${baseUrl}/api/user/subscription-status`;
        headers['Authorization'] = `Bearer ${token}`;
      }

      accountKey = accountId ?? NO_ACCOUNT_ID;
      loadedForThisAccount = loadedAccountRef.current === accountKey;
      if (refreshShowsLoading(options, loadedForThisAccount)) {
        setIsLoading(true);
      }

      // One request per account however many screens ask at once (fetchStatusShared).
      const result = await fetchStatusShared(`${url}|${accountKey}`, url, headers);

      // Bail out if the component unmounted during the fetch
      if (signal?.cancelled) return;

      if (!result.ok) {
        failedHttpStatus = result.httpStatus;
        throw new Error(`Server returned ${result.httpStatus}: ${result.text}`);
      }

      const data = result.data;

      applyStatus(data);
      if (Platform.OS !== 'web') {
        // The one onboarding fact the chat screen obeys — tagged with the
        // account this credential belongs to, so an answer for one account is
        // never read for another on the same phone.
        const answer = answerFromSubscriptionStatus(cedarJwt, data);
        if (answer) {
          recordOnboardingAnswer(answer);
        } else {
          console.warn('⚠️ SUBSCRIPTION: status carried no usable onboarding answer — onboarding gates stay unknown');
        }
      }
      // Decision 18: the app mirrors the backend-stamped cohort (fire-and-forget, deduped).
      void mirrorCedarCohort(data.cedar_cohort);
      staleRef.current = false;
      unavailableRef.current = false;
      setStatusUnavailable(false);
      setStatusStale(false);
      retryAttemptRef.current = 0;
      clearRetryTimer();
      if (accountId) {
        // Saved for the one moment it is needed: a first load that fails (a cold
        // open offline). Fire-and-forget; a storage failure costs nothing now.
        void storage.setItem(snapshotKey(accountId), encodeSnapshot(accountId, data)).catch(() => undefined);
      }
      loadedAccountRef.current = accountKey;

      if (__DEV__) console.log('✅ SUBSCRIPTION: tier:', data.tier, 'status:', data.status);

      // On native, initialize RevenueCat and cross-check entitlements.
      // If RevenueCat shows active entitlements but backend is stale (e.g. missed
      // webhook), trust RevenueCat — this prevents false paywall triggers on app
      // restart / re-login.
      if (Platform.OS !== 'web' && currentUser?.id) {
        try {
          await subscriptionService.initialize(currentUser.id);
          const customerInfo = await subscriptionService.getCustomerInfo();
          if (customerInfo) {
            const hasExtended = !!customerInfo.entitlements.active['extended_plan'];
            const hasFull = !!customerInfo.entitlements.active['full_plan'];
            const hasAnyActive = Object.keys(customerInfo.entitlements.active).length > 0
                                 || customerInfo.activeSubscriptions.length > 0;

            if (hasAnyActive && data.tier === 'trial' && !data.is_beta_tester) {
              const hasBasic = !!customerInfo.entitlements.active['basic_plan'];
              // 🩺 MAY 13 2026 — pdrtest22 audit: the fallback default
              // here was `'basic'`, which misclassified trial users with
              // ambiguous/sandbox active entitlements as Essentials. The
              // downstream effect: `isEssentialsDeactivated` flipped TRUE
              // and the "Make a change to the plan" button rendered for a
              // trial user, who then tapped it and triggered an unwanted
              // new touchpoint right after Cedar had said "Done" on a
              // prior recal. Root-cause fix: when the RevenueCat
              // entitlement set is non-empty but doesn't explicitly match
              // any known plan, preserve the backend's `'trial'` rather
              // than guessing `'basic'`. Backend remains the source of
              // truth; the override only takes effect when RC has a
              // RECOGNIZED plan entitlement.
              const rcTier: 'extended' | 'full' | 'basic' | null =
                hasExtended ? 'extended' : hasFull ? 'full' : hasBasic ? 'basic' : null;
              if (rcTier) {
                if (__DEV__) console.log('🔄 SUBSCRIPTION: RevenueCat override — backend says trial but RC has active entitlement, using tier:', rcTier);
                setTier(rcTier);
                setStatus('active');
                setCohort('paid_active');
              } else {
                if (__DEV__) console.log('🔄 SUBSCRIPTION: RC has active entitlement(s) but none match a known plan — preserving backend tier=trial');
              }
            }
          }
        } catch (e) {
          console.warn('RevenueCat cross-check failed (non-fatal):', e);
        }
      }

    } catch (error) {
      if (signal?.cancelled) return;
      const err = error instanceof Error ? error : new Error(String(error));
      // The server REFUSED this request (a 4xx other than a timeout or rate limit — an
      // expired or rejected credential, 401/403): asking again on a timer gets the same
      // answer, so the back-off stops (review of #4062). A foreground return, a change of
      // signed-in user or "Try again" still checks once.
      const retriesCanHelp = !failedHttpStatusIsPermanent(failedHttpStatus);
      if (!retriesCanHelp) {
        console.warn(`⚠️ SUBSCRIPTION: status refused (HTTP ${failedHttpStatus}) — not retrying on a timer`);
      }
      // A background re-check that fails keeps the status on screen: a network blip
      // must not repaint a lapsed payer or an expired user as a brand-new trial.
      if (refreshKeepsStatusOnError(options, loadedForThisAccount)) {
        console.warn('⚠️ SUBSCRIPTION: background refresh failed — keeping the last status');
        // A saved stand-in is still owed its fresh answer (review of #4062).
        if (staleRef.current && retriesCanHelp) scheduleRetry(signal);
        return;
      }
      // 🛡️ FIX (Mar 19, 2026): Downgrade network failures to console.warn
      // to avoid red LogBox screen on iOS simulator when backend isn't reachable yet.
      const isNetworkError = err.message.includes('Network request failed');
      if (isNetworkError) {
        console.warn('⚠️ SUBSCRIPTION: Network unavailable');
      } else {
        console.error('❌ SUBSCRIPTION: Check failed -', err.message);
      }
      // Never a trial pitch on a failure (review of #3988/#4031): an offline expired
      // user or lapsed payer used to read "Start your free 7-day trial" here. A status
      // already shown for this account stays; a first load restores this account's
      // saved status; with none, the status is UNAVAILABLE (neutral loading, a retry)
      // until a check works.
      let snapshot: Record<string, unknown> | null = null;
      if (!loadedForThisAccount && accountId) {
        try {
          snapshot = decodeSnapshot(await storage.getItem(snapshotKey(accountId)), accountId);
        } catch {
          snapshot = null;
        }
        if (signal?.cancelled) return;
      }
      const outcome = failedCheckOutcome(loadedForThisAccount, snapshot);
      if (outcome === 'restore') {
        console.warn('⚠️ SUBSCRIPTION: first load failed — showing this account\'s last saved status');
        applyStatus(snapshot);
        // Days left were counted when it was saved: count them from the dates now.
        setTrialDaysLeft(null);
        loadedAccountRef.current = accountKey;
        staleRef.current = true;
        unavailableRef.current = false;
        setStatusStale(true);
        setStatusUnavailable(false);
      } else if (outcome === 'unavailable') {
        stayLoading = true;
        // Another account's status may still be in this hook (a shared phone): clear it.
        if (loadedAccountRef.current !== null) {
          resetStatusToDefaults();
          loadedAccountRef.current = null;
        }
        staleRef.current = false;
        unavailableRef.current = true;
        setStatusStale(false);
        setStatusUnavailable(true);
      }
      // Still owed a fresh answer — nothing shown, or a saved status shown (review of
      // #4062: a stale status is retried until one check works, not just once).
      if (retriesCanHelp && (outcome !== 'keep' || staleRef.current)) scheduleRetry(signal);
    } finally {
      if (!signal?.cancelled && !stayLoading) {
        setIsLoading(false);
      }
      checkInFlightRef.current = false;

      // Cancelled — the screen unmounted, or the signed-in user changed (review of
      // #3988/#4031). The re-check queued meanwhile (a new user's first check is
      // queued behind this one) and its options must not go stale: clear both refs,
      // and run the queued check under ITS own signal if its screen still wants it.
      if (signal?.cancelled) {
        const queued = recheckNeededRef.current;
        const queuedOptions = recheckOptionsRef.current ?? undefined;
        const queuedSignal = recheckSignalRef.current;
        recheckNeededRef.current = false;
        recheckOptionsRef.current = null;
        recheckSignalRef.current = null;
        recheckCountRef.current = 0;
        if (queued && !(queuedSignal && queuedSignal.cancelled)) {
          void checkSubscription(queuedSignal ?? { cancelled: false }, queuedOptions);
        }
        return;
      }

      // If a re-check was requested while we were in flight, fire it now.
      // Depth counter limits recursion to 2 re-checks max per chain — always
      // reset AFTER the chain completes so future independent checks aren't blocked.
      if (recheckNeededRef.current && !signal?.cancelled) {
        recheckNeededRef.current = false;
        const queuedOptions = recheckOptionsRef.current ?? undefined;
        recheckOptionsRef.current = null;
        const queuedSignal = recheckSignalRef.current;
        recheckSignalRef.current = null;
        recheckCountRef.current = (recheckCountRef.current || 0) + 1;
        if (recheckCountRef.current <= 2) {
          checkSubscription(queuedSignal && !queuedSignal.cancelled ? queuedSignal : { cancelled: false }, queuedOptions);
        } else {
          // S1-13: Reset counter so the NEXT independent check chain can re-check
          // freely. Without this reset, a 3-failure chain blocks all future re-checks.
          recheckCountRef.current = 0;
        }
      } else {
        // 🔧 M35 FIX: Reset depth counter after a successful non-recursive check
        // so future re-check chains are not blocked by a stale counter.
        recheckCountRef.current = 0;
      }
    }
  }, [currentUser, applyStatus, clearRetryTimer, scheduleRetry, resetStatusToDefaults]);

  useEffect(() => {
    checkRef.current = checkSubscription;
  }, [checkSubscription]);

  // This instance's lifetime: timers it set stop with it.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      clearRetryTimer();
    };
  }, [clearRetryTimer]);

  // Back in the foreground with a stale or unavailable status: check now rather than
  // wait out the back-off (review of #4062) — the network often came back with it.
  useEffect(() => {
    if (Platform.OS === 'web') return undefined;
    const sub = AppState.addEventListener('change', (next) => {
      if (next !== 'active' || unmountedRef.current) return;
      if (!staleRef.current && !unavailableRef.current) return;
      clearRetryTimer();
      void checkRef.current(undefined, staleRef.current ? { background: true } : undefined);
    });
    return () => sub.remove();
  }, [clearRetryTimer]);

  useEffect(() => {
    const signal = { cancelled: false };

    if (__DEV__) {
      console.log('📦 SUBSCRIPTION: useEffect triggered, currentUser:', currentUser ? 'present' : 'undefined');
    }

    // On native, currentUser might not be populated on first mount, but we
    // have a JWT in secure storage. Re-check whenever currentUser changes
    // (e.g. after Auth0 loads) so the subscription reflects the latest state.
    if (Platform.OS !== 'web') {
      checkSubscription(signal);
    } else if (currentUser) {
      // Web: re-check whenever currentUser becomes available (handles async auth)
      checkSubscription(signal);
    }

    // Cleanup: mark cancelled so in-flight fetches don't call setState, and drop a
    // pending first-load retry (it belongs to this screen / this user).
    return () => {
      signal.cancelled = true;
      clearRetryTimer();
      retryAttemptRef.current = 0;
    };
  }, [currentUser, checkSubscription, clearRetryTimer]);

  // 🕖 Seven Free Days (2026-09-26): every screen holds its own copy of the
  // status, so a change the chat learns about mid-conversation (the 7-day clock
  // started on this turn) must reach them all — Account and the Subscription
  // drawer otherwise show the pre-trial copy until a relaunch.
  useEffect(() => {
    const sub = subscribeSubscriptionStatusChanged(() => {
      void checkSubscription();
    });
    return () => sub.remove();
  }, [checkSubscription]);

  // Account's re-read on every visit, its pull-to-refresh and its post-redeem refresh
  // (review of #3988): the status on screen stays until the new one arrives.
  const refreshSubscriptionInBackground = useCallback(
    () => checkSubscription(undefined, { background: true }),
    [checkSubscription],
  );

  /** "Try again" (Account, the Subscription drawer) when the status is unavailable:
   *  a check now; `retrying` is true until it settles (the button's busy state). */
  const retrySubscription = useCallback(async () => {
    clearRetryTimer();
    setRetrying(true);
    try {
      await checkSubscription(undefined, staleRef.current ? { background: true } : undefined);
    } finally {
      if (!unmountedRef.current) setRetrying(false);
    }
  }, [checkSubscription, clearRetryTimer]);

  const isPremium = tier === 'basic' || tier === 'full' || tier === 'extended';
  // 'free' is a legacy DB default (migration 070) — treat same as 'trial' for gate logic
  const isTrial = tier === 'trial' && (status === 'trial' || status === 'free');

  // hasActiveTrialOrSubscription: true if the user has started a trial (even if
  // tier is still 'trial') or has an active paid subscription.  Used by the trial
  // gate to distinguish "never subscribed" from "in active trial period".
  // 🔧 M35 FIX: Also true for beta testers (null dates) and isPremium (covers
  // RevenueCat override case where tier='full' but expiresAt is null).
  const hasActiveTrialOrSubscription =
    isBetaTester ||
    isPremium ||
    (trialExpiresAt != null && trialExpiresAt > new Date()) ||
    (expiresAt != null && expiresAt > new Date());

  return {
    tier,
    status,
    cohort,
    isLoading,
    statusUnavailable,
    statusStale,
    retrying,
    retrySubscription,
    isPremium,
    isTrial,
    isBetaTester,
    trialBoostActive,
    expiresAt,
    trialExpiresAt,
    trialStartsAt,
    hasActiveTrialOrSubscription,
    periodType,
    onboardingStep,
    onboardingCompleted,
    trialGateAtGoalConfirm,
    trialGateRequired,
    timeBoundTrial,
    lapsedPaid,
    freeTrialUsed,
    paywallProvider,
    cedarCohort,
    paywallVariables,
    summer50,
    freeMonth,
    storeFreeMonth,
    rewardsReady,
    trialDaysLeft,
    trialLengthDays,
    partnerOffer,
    refreshSubscription: checkSubscription,
    refreshSubscriptionInBackground,
  };
}
